import React from 'react';
import { Box, Paper, Popper, Typography, styled } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

const FLYOUT_OVERLAP_PX = 12;
const FLYOUT_HOVER_BRIDGE_PADDING_PX = 24;
const FLYOUT_POPPER_OFFSET: [number, number] = [0, -FLYOUT_OVERLAP_PX];

const FlyoutContainer = styled(Paper)({
  minWidth: 180,
  maxWidth: 280,
  backgroundColor: '#fff',
  borderRadius: '2px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
  overflow: 'hidden',
  maxHeight: '300px',
  overflowY: 'auto',
  scrollbarWidth: 'thin',
});

const FlyoutTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '13px',
  color: theme.palette.dianomiGreen.main,
  padding: '8px 16px',
  position: 'sticky',
  top: 0,
  backgroundColor: '#fff',
  borderBottom: `1px solid ${theme.palette.dianomiGrey.main}`,
  zIndex: 1,
}));

const FlyoutLoadingBox = styled(Box)({
  minHeight: '150px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const FlyoutSpinner = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.dianomiGreen.main,
}));

const HoverBridge = styled(Box)({
  paddingLeft: `${FLYOUT_HOVER_BRIDGE_PADDING_PX}px`,
  marginLeft: `-${FLYOUT_OVERLAP_PX}px`,
});

/**
 * menu content wrapper - styles child sidebar links.
 * scope MUI overrides to only menu area only
 */
const FlyoutMenuContent = styled(Box)(({ theme }) => ({
  // reset list container
  '& .MuiList-root': {
    padding: 0,
    margin: 0,
    backgroundColor: 'transparent',
  },

  // nested lists - visual hierarchy
  '& .MuiList-root .MuiList-root': {
    paddingLeft: '8px',
    backgroundColor: '#fafafa',
    borderLeft: '2px solid #e0e0e0',
    marginLeft: '8px',
  },

  // list items - reset
  '& .MuiListItem-root': {
    padding: 0,
    margin: 0,
    display: 'block',
    backgroundColor: 'transparent',
  },

  // links - full clickable area
  '& [role="button"], & a': {
    padding: '10px 16px',
    margin: 0,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    textDecoration: 'none',
    color: '#333',
    cursor: 'pointer',
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.palette.dianomiGrey.main,
    },
  },

  // text
  '& .MuiListItemText-root': {
    margin: 0,
    padding: 0,
  },

  '& .MuiTypography-root, & .MuiListItemText-primary': {
    fontSize: '13px',
    fontWeight: 500,
    color: '#333',
  },

  // hide any sidebar icons for children - cleaner UI
  '& .MuiListItemIcon-root, & svg, & img': {
    display: 'none',
  },

  // carets
  '& .MuiListItemText-secondary': {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },

  '& .MuiListItemText-secondary b': {
    display: 'inline-block',
    marginLeft: '12px',
  },
}));

interface NavFlyoutProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  title: string;
  children: React.ReactNode;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  isLoading?: boolean;
}

/**
 * NavFlyout - flyout menu for collapsed sidebar navigation.
 */
function NavFlyout({
  open,
  anchorEl,
  title,
  children,
  onMouseEnter,
  onMouseLeave,
  isLoading = false,
}: NavFlyoutProps) {
  if (!open || !anchorEl) {
    return null;
  }

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="right-start"
      style={{ zIndex: 1300 }}
      modifiers={[{ name: 'offset', options: { offset: FLYOUT_POPPER_OFFSET } }]}
    >
      <HoverBridge onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <FlyoutContainer elevation={8}>
          <FlyoutTitle>{title}</FlyoutTitle>
          {isLoading ? ( // if data load is slow, show spinner to avoid empty state
            <FlyoutLoadingBox>
              <FlyoutSpinner />
            </FlyoutLoadingBox>
          ) : (
            <FlyoutMenuContent>{children}</FlyoutMenuContent>
          )}
        </FlyoutContainer>
      </HoverBridge>
    </Popper>
  );
}

export default NavFlyout;
